import gql from 'graphql-tag';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';

import CartProducts from '../components/CartProducts';
import { ApolloClientFactory } from '../domain/apolloClientFactory';
import { Button, Container, Form, Section, Title, Icon, Svg } from '../modules/modules';
import CartService from '../services/CartService';
import FlashStore from '../services/FlashStore';
import { CurrencyFormatter as Currency } from '../utils/utils';
import CartNav from '../components/CartNav';
import AuthService from '../services/AuthService';
import CountryService from '../services/CountryService';
import VoucherUsed from '../modules/VoucherUsed';
import VatService from '../services/VatService';
import { vatDescription } from '../helpers/vatDescription';
import { GetVariantQuery } from '../components/Query/Products/FullVariantById';
import CartModalUnavailableProducts from '../modules/CartModalUnavailableProducts/CartModalUnavailableProducts';
import { GetFullVariantsBySkuQuery } from '../components/Query/Products/FullVariantsBySku';
import GoogleTagManagerService from '../services/GoogleTagManagerService';

interface ICartProps extends RouteComponentProps<{ language: string, sku?: string, voucher?: string }> { }

interface ICartState {
  unavailableProducts: any[];
  hilightProducts: boolean;
  modalOpen: boolean;
}

class Cart extends React.Component<ICartProps, ICartState> {

  private cartService = CartService.GetInstance();

  public constructor(props: ICartProps) {
    super(props);

    this.state = {
      unavailableProducts: [],
      hilightProducts: false,
      modalOpen: false,
    };

    this.setProduct = this.setProduct.bind(this);
    this.backToStore = this.backToStore.bind(this);
  }

  public toggleModal = () => {
    const modalState = this.state.modalOpen;
    return this.setState({ modalOpen: !modalState, hilightProducts: true });
  }

  public async componentDidMount() {
    const { match: { params: { voucher, sku } } } = this.props;

    if (sku) {
      await this.setProduct(sku);
    }

    if (voucher) {
      await this.setVoucher(voucher);
    }
  }

  public render() {
    const { VATrate } = CountryService.GetInstance();
    const { unavailableProducts, modalOpen, hilightProducts } = this.state;
    GoogleTagManagerService.productsInCart(this.cartService.orderItems);

    return (
      <main>
        <Section type="shopping-cart">
          <Container>
            <div className="cart">

              <CartNav step={1} />

              <Title><h2>Cart</h2></Title>

              {!this.cartService.isEmpty() ? (
                <React.Fragment>
                  <CartProducts
                    updateUnavailableProducts={this.updateUnavailableProducts}
                    hilightProducts={hilightProducts}
                  />

                  {/* {this.cartService.isFreeDeliveryAvailable() && 
                    <div className="text-shipping text-shipping--full">
                      Pro tuto objednávku máte dopravu Zdarma
                    </div>} */}

                  <div className="cart__footer cf">

                    {!CartService.GetInstance().voucherTitle
                      ? <Form onVoucher={voucher => this.setVoucher(voucher)} type="discount" />
                      : <VoucherUsed onClearVoucher={this.clearVoucher} />}

                    <div className="clearfix">
                      <div className="cart__totalPrice">
                        Total price:
                        <Currency price={CartService.GetInstance().totalPriceWithoutVatWithIndividualCustomerVoucherDiscount} decimals={2} />
                      </div>
                      {VATrate > 0 &&
                        <div className="cart__totalPrice withoutVat">
                          Total price {VatService.GetInstance().displayIncludeVat()}:{' '}
                          <Currency
                            price={CartService.GetInstance().totalPriceWithIndividualCustomerVoucherDiscount}
                          />
                        </div>}
                    </div>
                  </div>
                  <div className="nav-buttons">
                    <Button onClick={this.backToStore} type="return btn--fullMd">
                      Continue shopping
                    </Button>
                    <Button onClick={this.goToPersonData} type="proceed btn--fullMd">
                      Personal details
                    </Button>
                  </div>
                </React.Fragment>
              ) : (
                <div className="cart__empty">Shopping cart is empty</div>
              )}
            </div>
          </Container>
        </Section>

        {/* {false && <SimilarProducts type="cart" products={similarProducts} />} */}
        <div className="borderBottom pt-2" />
        {/* <button onClick={() => this.toggleModal()}>oetvři</button> */}
        {modalOpen && <CartModalUnavailableProducts
          closeModal={this.toggleModal}
          unavailableProducts={unavailableProducts}
          goToPersonData={this.forceGoToPersonData}
        />}
      </main>
    );
  }

  private async setProduct(sku: string) {
    const client = ApolloClientFactory.GetClient();
    const country = CountryService.GetInstance();
    const cart = CartService.GetInstance();
    const response = await client.query({
      query: GetFullVariantsBySkuQuery,
      variables: {
        skuIn: [sku],
        countryId: country && country.countryId,
        currencyId: country && country.currency && country.currency.id
      },
      fetchPolicy: 'network-only'
    });

    const { data } = response;
    if (data && Array.isArray(data.variants) && data.variants[0]) {
      await cart.removeEverything();
      await cart.addToCart(data.variants[0], undefined, 1);
    }
  }

  private async setVoucher(voucherCode: string) {

    const client = ApolloClientFactory.GetClient();

    const query = gql`
      query GetVoucherByCode ($voucherTitle: String!) {
        voucher(voucherTitle: $voucherTitle) {
          id
          pohodaIDS
          title
          voucherGroup {
            id
            title
            source
            type
            amount
            fixDiscountAmount

            giftId
            minimumPriceOfSingleItem
            maximumPriceOfSingleItem
            minimumPriceOfOrder
            maximumPriceOfOrder

            minimumPricesOfSingleItem {
              currencyId
              priceWithout
              recommendedPriceWithout
            }
            maximumPricesOfSingleItem {
              currencyId
              priceWithout
              recommendedPriceWithout
            }
            minimumPricesOfOrder {
              currencyId
              priceWithout
              recommendedPriceWithout
            }
            maximumPricesOfOrder {
              currencyId
              priceWithout
              recommendedPriceWithout
            }
            applyToDiscountedGoods
            includedProductTags
            excludedProductTags
            includedProducts
            freeShipping {
              id
            }
            customerGroups {
              id
            }
            customerIds
          }
          amountValue
          customerId
        }
      }
    `;

    const response = await client.query({
      query,
      variables: { voucherTitle: voucherCode },
      fetchPolicy: 'network-only'
    });

    const voucher = (response.data as any).voucher as GQL.IVoucher;

    let gift = null;
    if (voucher && voucher.voucherGroup
      && voucher.voucherGroup.giftId
      && voucher.voucherGroup.type === 'gift'
    ) {
      const result = await client.query({
        query: GetVariantQuery,
        variables: { id: voucher.voucherGroup.giftId },
        fetchPolicy: 'network-only',
      });

      gift = (result && result.data && result.data.variant) || null;
    }

    if (voucher) {
      let canBeApplied: boolean = true;
      if (voucher.amountValue) {
        const discountValue = CartService.getDirectVoucherDiscountValue(voucher);
        canBeApplied = (!!discountValue) && (discountValue > 0);
        if (!canBeApplied) {
          FlashStore.GetInstance().showMessage(`Voucher ${voucherCode} cannot be applied`, 'warning');
        }
      }
      if (canBeApplied) {
        FlashStore.GetInstance().showMessage(`Voucher ${voucherCode} successfully applied`, 'success');
        CartService.GetInstance().setVoucher(voucher, gift);
      }
    } else {
      FlashStore.GetInstance().showMessage(`Voucher ${voucherCode} not found`, 'error');
    }
  }

  private async clearVoucher() {
    FlashStore.GetInstance().showMessage(`Voucher has been removed.`, 'success');
    CartService.GetInstance().clearVoucher();
  }

  private backToStore() {
    this.props.history.goBack();
  }

  private goToPersonData = () => {
    const { countryCode } = CountryService.GetInstance();
    const { unavailableProducts, hilightProducts } = this.state;
    const { history: { push, location: { pathname } } } = this.props;

    if (!this.checkIfPersonIsValid()) {
      FlashStore.GetInstance().showMessage('If you want use your profile, fill required information please!', 'warning');
      return push(`/${countryCode}/user/data?returnTo=${pathname}`);
    }

    if (unavailableProducts.length > 0 && !hilightProducts) {
      return this.setState({ modalOpen: true });
    }

    GoogleTagManagerService.beginCheckout(this.cartService.orderItems);

    // Country code urls are not used anymore
    // return push(`/${countryCode}/personal-data`);
    return push('/personal-data');
  }

  private forceGoToPersonData = () => {
    const { countryCode } = CountryService.GetInstance();
    const { history: { push } } = this.props;
    if (this.checkIfPersonIsValid()) {
      // Country code urls are not used anymore
      // return push(`/${countryCode}/personal-data`);
      return push('/personal-data');
    }

    return this.redirectToPersonSettingsWithAlert();
  }

  private redirectToPersonSettingsWithAlert = () => {
    const { history: { push } } = this.props;
    const { countryCode } = CountryService.GetInstance();

    FlashStore.GetInstance().showMessage('If you want use your profile, fill required information please!', 'warning');
    return push(`/${countryCode}/user/data`);
  }

  private checkIfPersonIsValid = () => {
    const authService = AuthService.GetInstance();
    if (authService.loggedIn) {
      const { invoiceFirstName, invoiceLastName, invoiceCity, invoiceZip, phone } = AuthService.GetInstance().customer as any;
      return [
        invoiceFirstName,
        invoiceLastName,
        invoiceCity,
        invoiceZip,
        phone,
      ].every((v: string | undefined | null) => !!v);
    }

    return true;
  }
  private updateUnavailableProducts = (products: any) => {
    this.setState({
      unavailableProducts: products,
    });
  }
}

export default observer(Cart);



// WEBPACK FOOTER //
// ./src/pages/Cart.tsx